// London Smoke & Cure — customer pricing flow (catalogue → cart → OrderDesk).
//
// Single source for tier-scoped unit prices, cart line shape, and checkout revalidation.
// Uses products_{pricingKey} as authoritative price data; honours sale windows via productSaleUtils.

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
    normalizePricingKey,
    productsCollectionName,
} from '../services/pricingTierService';
import { isProductMuted } from './productCatalogVisibility';
import {
    mergeOrderableWithEnquiryLines,
    partitionCartItems,
} from './cartEnquiryLines';
import { getProductImageUrls } from '../services/productGalleryService';
import { getEffectivePricing } from './productSaleUtils';
import { roundMoneyGbp } from './wholesaleShippingUtils';

const GBP_FORMAT = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

/** Primary catalogue photo for cart stamps (ImageURLs[0] / Image URL / decorated imageUrl). */
function resolveCartStampImageUrl(product, fallback = '/placeholder-product.svg') {
    const primary = getProductImageUrls(product)[0]
        || String(product?.imageUrl || product?.['Image URL'] || '').trim();
    return primary || fallback;
}

/** Format a GBP unit price for customer-facing copy. */
export function formatUnitPriceGbp(amount) {
    return GBP_FORMAT.format(roundMoneyGbp(Number(amount) || 0));
}

/** Normalize SKU for cart / tier map lookups. */
export function normalizeCartSku(code) {
    return String(code ?? '').trim();
}

/**
 * Stamp a catalogue product for cart storage. Customer tier wins when known;
 * checkout revalidation against products_{tier} is authoritative (no add-time reject on stale stamps).
 * @returns {object|null}
 */
export function resolveCartStampPricingKey(product, customerPricingKey) {
    const code = normalizeCartSku(product?.code);
    if (!code) return null;

    const customerKey = customerPricingKey
        ? normalizePricingKey(customerPricingKey)
        : null;
    const productKey = product?.pricingKey
        ? normalizePricingKey(product.pricingKey)
        : null;
    const tierKey = customerKey || productKey;
    if (!tierKey) return null;

    return {
        ...product,
        code,
        pricingKey: tierKey,
        priceListSynced: Boolean(customerKey),
        imageUrl: resolveCartStampImageUrl(product),
    };
}

/** Map a Firestore products_{tier} row into productSaleUtils shape. */
export function firestoreRowToPricingProduct(row) {
    if (!row || typeof row !== 'object') return {};
    return {
        ...row,
        price: row.Price ?? row.price,
        Price: row.Price ?? row.price,
        onSale: row.onSale,
        salePrice: row.salePrice,
        previousPrice: row.previousPrice,
        saleStartAt: row.saleStartAt,
        saleEndAt: row.saleEndAt,
    };
}

/**
 * Effective unit price (GBP) for a tier product row, including active sale windows.
 */
export function resolveEffectiveUnitPriceGbp(firestoreRow, nowMs = Date.now()) {
    const { price } = getEffectivePricing(firestoreRowToPricingProduct(firestoreRow), nowMs);
    return roundMoneyGbp(price);
}

/**
 * Build a cart line from an authoritative Firestore tier row.
 * @param {object} firestoreRow — doc from products_{key}
 * @param {string} pricingKey — normalized tier key
 * @param {{ quantity?: number, unitPrice?: number, nowMs?: number }} [options]
 */
export function buildCartLineFromTierProduct(firestoreRow, pricingKey, options = {}) {
    const code = normalizeCartSku(firestoreRow?.Code);
    const quantity = Math.max(1, Number(options.quantity) || 1);
    const unitPrice =
        options.unitPrice != null
            ? roundMoneyGbp(options.unitPrice)
            : resolveEffectiveUnitPriceGbp(firestoreRow, options.nowMs);

    const rawStock = String(firestoreRow?.Stock ?? '').trim();
    let stock;
    if (rawStock.toLowerCase() === 'x') {
        stock = 'x';
    } else if (rawStock !== '') {
        const parsed = parseInt(rawStock, 10);
        stock = Number.isNaN(parsed) ? undefined : parsed;
    }

    const minOrderParsed = parseInt(firestoreRow?.['Min Order'], 10);
    const minOrder = Number.isNaN(minOrderParsed) ? 1 : Math.max(1, minOrderParsed);

    return {
        name: String(firestoreRow?.Name || '').trim() || code,
        code,
        price: unitPrice,
        quantity,
        stock,
        packSize: firestoreRow?.['Pack Size'] || '',
        minOrder,
        pricingKey: normalizePricingKey(pricingKey),
        imageUrl: resolveCartStampImageUrl(firestoreRow, ''),
        onSale: firestoreRow?.onSale,
        salePrice: firestoreRow?.salePrice,
        previousPrice: firestoreRow?.previousPrice,
        saleStartAt: firestoreRow?.saleStartAt,
        saleEndAt: firestoreRow?.saleEndAt,
    };
}

/**
 * OrderDesk / Firestore order_items line from a validated cart line.
 * Do not set `id` to the array index. OrderDesk treats an item id as its own
 * line id: "0" / "1" are added again on the next update instead of matching.
 */
export function buildOrderDeskLineFromCartLine(cartLine) {
    return {
        name: cartLine.name,
        price: roundMoneyGbp(cartLine.price),
        quantity: Math.max(1, Number(cartLine.quantity) || 1),
        code: normalizeCartSku(cartLine.code),
        stock: cartLine.stock,
        packSize: cartLine.packSize,
    };
}

/** Admin / reorder picker rows with effective tier prices. */
export function mapTierRowsToPickerProducts(rows, pricingKey) {
    const key = normalizePricingKey(pricingKey);
    return (rows || []).map((row) => ({
        id: normalizeCartSku(row.Code),
        name: row.Name,
        code: normalizeCartSku(row.Code),
        price: resolveEffectiveUnitPriceGbp(row),
        minOrder: parseInt(row['Min Order'], 10) || 1,
        pricingKey: key,
    }));
}

/**
 * Load SKU-keyed map of catalogue-visible tier products (muted omitted).
 * @returns {Promise<Map<string, object>>}
 */
export async function loadTierProductMap(pricingKey) {
    const key = normalizePricingKey(pricingKey);
    const collectionName = productsCollectionName(key);
    const snap = await getDocs(collection(db, collectionName));
    const map = new Map();

    snap.forEach((d) => {
        const data = { ...(d.data() || {}) };
        if (!data.Code) data.Code = d.id;
        if (isProductMuted(data)) return;
        const name = String(data.Name || '').trim();
        const priceRaw = String(data.Price ?? '').trim();
        if (!name || !priceRaw) return;
        map.set(normalizeCartSku(data.Code), data);
    });

    return map;
}

/**
 * Revalidate cart lines against an in-memory tier map (testable without Firestore).
 * @returns {{
 *   ok: boolean,
 *   lines: object[],
 *   errors: Array<{ code: string, reason: string, message: string }>,
 *   priceCorrections: Array<{ code: string, was: number, now: number }>,
 *   pricingKey: string,
 *   message: string,
 * }}
 */
export function revalidateCartLinesWithTierMap(cartItems, tierMap, pricingKey, nowMs = Date.now()) {
    const key = normalizePricingKey(pricingKey);
    const errors = [];
    const lines = [];
    const priceCorrections = [];

    for (const item of cartItems || []) {
        const code = normalizeCartSku(item?.code);
        if (!code) {
            errors.push({
                code: '',
                reason: 'missing_sku',
                message: 'A cart item is missing a product code.',
            });
            continue;
        }

        const row = tierMap.get(code);
        if (!row) {
            errors.push({
                code,
                reason: 'not_on_list',
                message: `"${item.name || code}" is not on your price list.`,
            });
            continue;
        }

        const unitPrice = resolveEffectiveUnitPriceGbp(row, nowMs);
        if (!Number.isFinite(unitPrice) || unitPrice < 0) {
            errors.push({
                code,
                reason: 'no_price',
                message: `"${item.name || code}" has no valid price on your list.`,
            });
            continue;
        }

        const qty = Math.max(1, Number(item.quantity) || 1);
        const cartPrice = roundMoneyGbp(Number(item.price) || 0);
        if (Math.abs(unitPrice - cartPrice) > 0.005) {
            priceCorrections.push({ code, was: cartPrice, now: unitPrice });
        }

        lines.push({
            ...buildCartLineFromTierProduct(row, key, {
                quantity: qty,
                unitPrice,
                nowMs,
            }),
            priceListSynced: item.priceListSynced === true,
        });
    }

    if (errors.length) {
        return {
            ok: false,
            lines: [],
            errors,
            priceCorrections: [],
            pricingKey: key,
            message: errors.map((e) => e.message).join(' '),
        };
    }

    return {
        ok: true,
        lines,
        errors: [],
        priceCorrections,
        pricingKey: key,
        message: '',
    };
}

/**
 * Revalidate cart against the customer's assigned tier (Firestore products_{key}).
 * Enquiry reminder lines are preserved and never checked against the purchasable tier map.
 */
export async function revalidateCartAgainstTier(cartItems, pricingKey, options = {}) {
    const key = normalizePricingKey(pricingKey);
    const { orderable, enquiries } = partitionCartItems(cartItems);
    if (!orderable.length) {
        return {
            ok: true,
            lines: [...enquiries],
            errors: [],
            priceCorrections: [],
            pricingKey: key,
            message: '',
        };
    }
    const tierMap = await loadTierProductMap(key);
    const result = revalidateCartLinesWithTierMap(
        orderable,
        tierMap,
        key,
        options.nowMs ?? Date.now()
    );
    if (!result.ok) return result;
    return {
        ...result,
        lines: mergeOrderableWithEnquiryLines(result.lines, enquiries),
    };
}

/**
 * Price corrections that should surface the "changed since added" notice.
 * Suppresses drift from provisional loads (missing tier stamp or never synced to live tier).
 */
export function filterActionablePriceCorrections(priceCorrections, cartItems = [], pricingKey) {
    const tierKey = normalizePricingKey(pricingKey);
    const byCode = new Map(
        (cartItems || []).map((item) => [normalizeCartSku(item?.code), item])
    );

    return (priceCorrections || []).filter((correction) => {
        const prev = byCode.get(normalizeCartSku(correction?.code));
        if (!prev) return false;
        if (!prev.priceListSynced) return false;
        const stamp = prev.pricingKey ? normalizePricingKey(prev.pricingKey) : null;
        if (!stamp || stamp !== tierKey) return false;
        return true;
    });
}

/**
 * Attach product names to price correction rows for UI copy.
 * @param {Array<{ code: string, was: number, now: number, name?: string }>} priceCorrections
 * @param {object[]} [cartItems]
 */
export function enrichPriceCorrections(priceCorrections, cartItems = []) {
    const nameByCode = new Map();
    (cartItems || []).forEach((item) => {
        const code = normalizeCartSku(item?.code);
        if (code) nameByCode.set(code, String(item.name || code).trim());
    });
    return (priceCorrections || []).map((correction) => ({
        ...correction,
        name:
            correction.name ||
            nameByCode.get(normalizeCartSku(correction.code)) ||
            correction.code,
    }));
}

/** Multi-line summary of unit price changes (was → now). */
export function describeCartPriceCorrections(priceCorrections, cartItems = []) {
    const enriched = enrichPriceCorrections(priceCorrections, cartItems);
    if (!enriched.length) return '';
    return enriched
        .map(
            (c) =>
                `${c.name}: ${formatUnitPriceGbp(c.was)} → ${formatUnitPriceGbp(c.now)}`
        )
        .join('\n');
}

/**
 * Banner / alert payload when cart lines were refreshed to current tier prices.
 * @returns {{ title: string, message: string, details: string, corrections: object[] } | null}
 */
export function buildCartPriceChangeNotice(priceCorrections, cartItems = []) {
    const enriched = enrichPriceCorrections(priceCorrections, cartItems);
    if (!enriched.length) return null;
    return {
        title: 'Prices updated on your price list',
        message:
            'Some prices have changed since these items were added to your cart. Your cart now uses the current prices from your price list.',
        details: describeCartPriceCorrections(enriched),
        corrections: enriched,
    };
}

/**
 * Order history total — always from stored order fields (what the customer paid), never live catalogue.
 */
export function resolveHistoricalOrderTotal(orderData) {
    if (!orderData || typeof orderData !== 'object') return '0.00';
    const stored = orderData.order_total;
    if (stored != null && String(stored).trim() !== '') {
        return roundMoneyGbp(Number(stored)).toFixed(2);
    }
    const itemsTotal = (orderData.order_items || []).reduce(
        (sum, it) =>
            sum +
            roundMoneyGbp(Number(it.price) || 0) * Math.max(1, Number(it.quantity) || 1),
        0
    );
    const shipping = roundMoneyGbp(Number(orderData.shipping_total) || 0);
    const handling = roundMoneyGbp(Number(orderData.handling_total) || 0);
    const tax = roundMoneyGbp(Number(orderData.tax_total) || 0);
    return roundMoneyGbp(itemsTotal + shipping + handling + tax).toFixed(2);
}

/**
 * Compare historical order line prices with current tier rows (reorder prompt).
 * @param {object[]} orderItems — stored order_items from a past order
 * @param {Record<string, object>} rowByCode — SKU → Firestore products_{tier} row
 */
export function compareOrderItemsToTierRows(orderItems, rowByCode, pricingKey, nowMs = Date.now()) {
    const priceCorrections = [];
    for (const it of orderItems || []) {
        const code = normalizeCartSku(it?.code);
        if (!code) continue;
        const row = rowByCode[code];
        if (!row) continue;
        const was = roundMoneyGbp(Number(it.price) || 0);
        const now = resolveEffectiveUnitPriceGbp(row, nowMs);
        if (Math.abs(now - was) > 0.005) {
            priceCorrections.push({
                code,
                was,
                now,
                name: String(it.name || code).trim(),
            });
        }
    }
    return {
        priceCorrections,
        pricingKey: normalizePricingKey(pricingKey),
    };
}
